/**
 * Utility functions for page-specific widget management
 * Handles unique widget IDs with page names for easy identification
 */

/**
 * Supported page types for page-specific widgets
 * Single source of truth for all page names
 */
export const PAGE_TYPES = ['events', 'story', 'gallery', 'blog', 'contact'] as const;

/**
 * Generate unique widget ID with page name
 * Format: WidgetType_PageName_UniqueID
 * @example generatePageWidgetId("LeftContentRightMedia", "events") 
 * // Returns: "LeftContentRightMedia_events_A1B2C3D4"
 */
export const generatePageWidgetId = (widgetType: string, pageName: string): string => {
  const uuid = crypto.randomUUID();
  const shortId = uuid.split('-')[0].toUpperCase();
  return `${widgetType}_${pageName}_${shortId}`;
};

/**
 * Extract base widget type from page widget type
 * Handles multiple formats for backward compatibility
 * @example 
 * extractBaseType("LeftContentRightMedia_events_A1B2C3D4") // "LeftContentRightMedia"
 * extractBaseType("events_LeftContentRightMedia") // "LeftContentRightMedia"
 * extractBaseType("BasicBanner") // "BasicBanner"
 */
export const extractBaseType = (type: string): string => {
  if (!type || !type.includes('_')) {
    return type;  // Simple type without underscore, return as-is
  }
  
  const parts = type.split('_');
  
  // New format: "LeftContentRightMedia_events_A1B2C3D4" (3 parts)
  if (parts.length === 3) {
    return parts[0];  // Return widget type
  }
  
  // Old/legacy format: "events_LeftContentRightMedia" (2 parts)
  if (parts.length === 2) {
    // Check if first part is likely page name (all lowercase)
    const firstPart = parts[0];
    const isPageName = firstPart === firstPart.toLowerCase();
    
    if (isPageName) {
      return parts[1];  // Return widget type (second part)
    }
    return parts[0];  // Otherwise first part is widget type
  }
  
  // Fallback: return original type
  return type;
};

/**
 * Extract page name from widget type
 * @example 
 * extractPageName("LeftContentRightMedia_events_A1B2C3D4") // "events"
 * extractPageName("events_LeftContentRightMedia") // "events"
 */
export const extractPageName = (type: string): string | null => {
  if (!type || !type.includes('_')) {
    return null;
  }
  
  const parts = type.split('_');
  
  // New format: "LeftContentRightMedia_events_A1B2C3D4"
  if (parts.length === 3) {
    return parts[1];  // Page name is middle part
  }
  
  // Old format: "events_LeftContentRightMedia"
  if (parts.length === 2) {
    const firstPart = parts[0];
    // If first part is lowercase, it's likely the page name
    if (firstPart === firstPart.toLowerCase()) {
      return firstPart;
    }
  }
  
  return null;
};

/**
 * Check if widget belongs to specific page
 * Supports multiple formats for backward compatibility:
 * 1. New format: type contains "_pageName_" 
 * 2. pageType field (legacy)
 * 3. Old format: type contains page name
 * 
 * @example
 * isPageWidget({ type: "LeftContentRightMedia_events_A1B2C3D4" }, "events") // true
 * isPageWidget({ type: "events_LeftContentRightMedia" }, "events") // true
 * isPageWidget({ type: "BasicBanner", pageType: "events" }, "events") // true
 */
export const isPageWidget = (widget: any, pageName: string): boolean => {
  if (!widget || !widget.type) {
    return false;
  }
  
  // Format 1: New format with page name in type "_pageName_"
  // Example: "LeftContentRightMedia_events_A1B2C3D4"
  if (widget.type.includes(`_${pageName}_`)) {
    return true;
  }
  
  // Format 2: Legacy format with pageType field
  // Example: { type: "LeftContentRightMedia", pageType: "events" }
  if (widget.pageType === pageName) {
    return true;
  }
  
  // Format 3: Old format with page name at start "pageName_"
  // Example: "events_LeftContentRightMedia"
  if (widget.type.toLowerCase().startsWith(`${pageName.toLowerCase()}_`)) {
    return true;
  }
  
  // Format 4: Very old format - type just contains page name anywhere
  // Example: "eventsLeftContentRightMedia" or similar
  if (widget.type.toLowerCase().includes(pageName.toLowerCase())) {
    return true;
  }
  
  return false;
};
